import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  TrashIcon, 
  ArrowPathIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface CacheEntry {
  url: string;
  contentLength: number;
  contentPreview: string;
  cachedAt: string;
  ageMinutes: number;
}

interface CacheData {
  cache: CacheEntry[];
  totalEntries: number;
  totalSize: number;
}

export const CacheTab: React.FC = () => {
  const [cacheData, setCacheData] = useState<CacheData | null>(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState<string | null>(null);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);

  const fetchCacheData = async () => {
    try {
      const data = await apiService.getCacheContents();
      setCacheData(data);
    } catch (error) {
      console.error('Failed to fetch cache data:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearCache = async (url?: string) => {
    const target = url || 'all';
    setClearing(target);
    
    try {
      await apiService.clearScrapingCache(url);
      await fetchCacheData();
    } catch (error) {
      console.error('Failed to clear cache:', error);
    } finally {
      setClearing(null);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAgeColor = (ageMinutes: number) => {
    if (ageMinutes < 5) return 'text-green-600';
    if (ageMinutes < 30) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getDomainFromUrl = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return 'Unknown';
    }
  };

  useEffect(() => {
    fetchCacheData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Scraping Cache</h2>
          <p className="text-sm text-gray-600 mt-1">
            Monitor and manage cached article content for debugging
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchCacheData()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => clearCache()}
            disabled={clearing === 'all'}
            className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
          >
            <TrashIcon className="w-4 h-4" />
            {clearing === 'all' ? 'Clearing...' : 'Clear All'}
          </button>
        </div>
      </div>

      {/* Cache Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Total Entries</span>
          </div>
          <div className="text-2xl font-bold text-blue-900 mt-1">
            {cacheData?.totalEntries || 0}
          </div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-900">Total Size</span>
          </div>
          <div className="text-2xl font-bold text-green-900 mt-1">
            {formatBytes(cacheData?.totalSize || 0)}
          </div>
        </div>
        
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-900">Cache TTL</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900 mt-1">
            60 min
          </div>
        </div>
      </div>

      {/* Cache Entries */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="font-medium text-gray-900">Cache Entries</h3>
        </div>
        
        {!cacheData?.cache.length ? (
          <div className="p-8 text-center text-gray-500">
            <DocumentTextIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No cache entries found</p>
          </div>
        ) : (
          <div className="divide-y">
            {cacheData.cache.map((entry, index) => (
              <div key={index} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {getDomainFromUrl(entry.url)}
                      </span>
                      <span className={`text-xs ${getAgeColor(entry.ageMinutes)}`}>
                        {entry.ageMinutes}m ago
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatBytes(entry.contentLength)}
                      </span>
                    </div>
                    
                    <div className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer mb-2 break-all">
                      <a href={entry.url} target="_blank" rel="noopener noreferrer">
                        {entry.url}
                      </a>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      Cached: {format(new Date(entry.cachedAt), 'PPp')}
                    </div>
                    
                    {expandedEntry === entry.url && (
                      <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                        <div className="font-medium text-gray-700 mb-2">Content Preview:</div>
                        <div className="text-gray-600 whitespace-pre-wrap font-mono text-xs">
                          {entry.contentPreview}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => setExpandedEntry(
                        expandedEntry === entry.url ? null : entry.url
                      )}
                      className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 border rounded"
                    >
                      {expandedEntry === entry.url ? 'Hide' : 'Preview'}
                    </button>
                    
                    <button
                      onClick={() => clearCache(entry.url)}
                      disabled={clearing === entry.url}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      <TrashIcon className="w-3 h-3" />
                      {clearing === entry.url ? 'Clearing...' : 'Clear'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};